import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	SECTION_SPEC_CONFIRMATION_ACTION,
	type SectionSpecPublication,
} from "@/shared/models/SectionSpec";
import {
	deleteSectionSpec,
	publishSectionSpec,
	unscheduleSectionSpec,
} from "@/shared/repositories/SectionSpecRepository";

interface UseSectionSpecMutationsOptions {
	sectionId: number;
	assetId: number;
	onError: (error: Error) => void;
	onDone: (message: string, profileSpecId: number | null) => void;
}

export const useSectionSpecMutations = ({
	sectionId,
	assetId,
	onError,
	onDone,
}: UseSectionSpecMutationsOptions) => {
	const queryClient = useQueryClient();

	const refresh = (profileSpecId: number) =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: ["section-spec", sectionId, assetId],
			}),
			queryClient.invalidateQueries({
				queryKey: ["section-spec-version", profileSpecId],
			}),
		]);

	const confirm = useMutation({
		mutationFn: (request: {
			profileSpecId: number;
			publication: SectionSpecPublication;
		}) => publishSectionSpec(request.profileSpecId, request.publication),
		onSuccess: async (result) => {
			await refresh(result.ProfileSpecId);
			onDone(
				result.ActionCode === SECTION_SPEC_CONFIRMATION_ACTION.IMAGES_UPDATED
					? `Images updated on Rev ${result.Rev}`
					: `Draft confirmed as Rev ${result.Rev}`,
				result.ProfileSpecId,
			);
		},
		onError,
	});

	const remove = useMutation({
		mutationFn: deleteSectionSpec,
		onSuccess: async (_, profileSpecId) => {
			await queryClient.invalidateQueries({
				queryKey: ["section-spec", sectionId, assetId],
			});
			queryClient.removeQueries({
				queryKey: ["section-spec-version", profileSpecId],
				exact: true,
			});
			onDone("Draft deleted", null);
		},
		onError,
	});

	const sendToDraft = useMutation({
		mutationFn: unscheduleSectionSpec,
		onSuccess: async (_, profileSpecId) => {
			await refresh(profileSpecId);
			onDone("Revision changed to Draft", profileSpecId);
		},
		onError: async (error: Error, profileSpecId) => {
			onError(error);
			await refresh(profileSpecId);
		},
	});

	return {
		confirm,
		remove,
		sendToDraft,
		refresh,
		isPending: confirm.isPending || remove.isPending || sendToDraft.isPending,
	};
};
